import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlertEnum, DialogEnum } from '../../../../components/messages/messages.enum';
import { SelectableOption } from '../../../../components/messages/option-selector-dialog/option-selector-dialog.component';
import { User } from '../../../../models/user.model';
import { MessageService } from '../../../../components/messages/message.service';
import { SessionStateService } from '../../../../services/session-state.service';
import { SystemService } from '../../../../services/system.service';
import { UserApiService } from '../../../../services/user-api.service';
import { UserConfigService } from '../../../../services/user-config.service';
import { UserSessionService } from '../../../../services/user-session.service';

/**
 * Control de sesión por tiempo de sesión (SessionTime) y por inactividad (InactivityTime).
 *
 * SessionTime (sessionTime):
 *   - Tiempo máximo que puede estar el usuario en el sistema (activo o inactivo), con opción a renovar.
 *   - Un minuto antes del límite se muestra "La sesión finalizará en: XX" con botón "Continuar sesión".
 *   - Solo al hacer clic en "Continuar sesión" se renueva la sesión (resume-session) y se amplía la hora límite.
 *   - La alerta de sesión solo se cierra cuando: (1) termina el tiempo, o (2) el usuario renueva (clic en "Continuar sesión").
 *   - Al volver al portal NO se cierra la alerta, para que el usuario pueda seguir viendo y hacer clic en "Continuar sesión".
 *
 * InactivityTime (inactivityTime):
 *   - Tiempo sin actividad (mouse, teclado, scroll, click) antes de mostrar aviso.
 *   - Tras X minutos sin actividad se muestra "La sesión se cerrará por inactividad en: XX".
 *   - Al volver al portal: se cierra la alerta y SÍ se reinicia el conteo de inactividad (a diferencia de SessionTime).
 *
 * resume-session: se llama ÚNICAMENTE cuando el usuario hace clic en "Continuar sesión" en la alerta de SessionTime.
 */
@Injectable({ providedIn: 'root' })
export class SessionControlService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly sessionState = inject(SessionStateService);
  private readonly systemService = inject(SystemService);
  private readonly messageService = inject(MessageService);
  private readonly userApiService = inject(UserApiService);
  private readonly userConfigService = inject(UserConfigService);
  private readonly userSessionService = inject(UserSessionService);

  private inactivityTimerId: ReturnType<typeof setTimeout> | null = null;
  private sessionTimerId: ReturnType<typeof setTimeout> | null = null;
  private isExpiryWarningArmed = false;
  private activeTimerReason: 'inactivity' | 'session' | null = null;
  private maskedSessionChoiceResolved = false;
  private maskedSessionChoiceInFlight = false;

  private get sessionStartKey(): string {
    return this.systemService.localStorageKeys.sessionStartUtcMs;
  }

  constructor() {
    this.sessionState.startIdleCount
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (value) this.refreshSessionTimers();
      });

    this.sessionState.sessionEnded
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ended) => {
        if (!ended) return;
        this.clearSessionTimers();
        if (this.isExpiryWarningArmed) {
          this.messageService.dismissTimerAlert();
          this.isExpiryWarningArmed = false;
          this.activeTimerReason = null;
        }
      });

    this.userConfigService.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user: User) => {
        this.applyUserLanguage(user);
        this.refreshSessionTimers();
        void this.promptMaskedSessionChoiceIfNeeded(user);
      });
  }

  onUserActivity(): void {
    this.sessionState.userActivity.next(Date.now());

    if (!this.hasSession() || this.isPermanentSession()) return;

    if (this.isExpiryWarningArmed) {
      if (this.activeTimerReason === 'inactivity') {
        // InactivityTime: cerrar alerta y reiniciar conteo de inactividad (refreshSessionTimers más abajo).
        this.messageService.dismissTimerAlert();
        this.isExpiryWarningArmed = false;
        this.activeTimerReason = null;
      } else if (this.activeTimerReason === 'session') {
        // SessionTime: NO cerrar la alerta al volver al portal. La alerta solo se cierra cuando
        // expire el tiempo o el usuario haga clic en "Continuar sesión". Así el usuario puede renovar.
        return;
      }
    }

    // Reinicia el timer de inactividad (y vuelve a programar el de sesión con la misma hora límite).
    this.refreshSessionTimers();
  }

  refreshSessionTimers(): void {
    this.clearSessionTimers();
    this.scheduleInactivityTimer();
    this.scheduleSessionTimer();
  }

  private async promptMaskedSessionChoiceIfNeeded(user: User): Promise<void> {
    const isMasked = !!user?.maskedUser;
    if (!isMasked) {
      this.maskedSessionChoiceResolved = false;
      return;
    }

    if (this.maskedSessionChoiceResolved || this.maskedSessionChoiceInFlight) return;

    const maskedName = user?.maskedUser?.person?.name ?? '';
    const mainName = user?.currentUser?.person?.name ?? '';

    this.maskedSessionChoiceInFlight = true;
    try {
      const title = 'Sesión enmascarada';
      const message =
        'Tu usuario está enmascarado<br>¿Con qué sesión deseas continuar?';

      const optMaskedLabel = `Continuar como ${maskedName || 'Usuario enmascarado'}`;
      const optMainLabel = `Continuar como ${mainName || 'Usuario principal'}`;

      const options: SelectableOption[] = [
        { id: 1, name: optMaskedLabel },
        { id: 2, name: optMainLabel },
      ];

      const selectedId = await this.messageService.optionSelectorDialog(
        DialogEnum.Information,
        title,
        message,
        options,
        false,
      );

      if (selectedId === 2) {
        await this.userSessionService.logOutMask();
        return;
      }
    } finally {
      this.maskedSessionChoiceResolved = true;
      this.maskedSessionChoiceInFlight = false;
    }
  }

  private scheduleInactivityTimer(): void {
    if (!this.hasSession() || this.isPermanentSession()) return;

    const inactivityMinutes = Number(this.systemService.user?.userType?.inactivityTime ?? 0);
    if (!inactivityMinutes || inactivityMinutes <= 0) return;

    const inactivityMs = inactivityMinutes * 60 * 1000;
    const warningSeconds = Math.max(1, Math.min(60, Math.floor(inactivityMs / 1000)));
    const warningDelayMs = Math.max(0, inactivityMs - warningSeconds * 1000);

    this.inactivityTimerId = setTimeout(() => {
      if (
        !this.hasSession() ||
        this.isPermanentSession() ||
        this.isExpiryWarningArmed
      )
        return;

      this.isExpiryWarningArmed = true;
      this.activeTimerReason = 'inactivity';

      void this.messageService
        .timerAlert(
          AlertEnum.Warning,
          'La sesión se cerrará por inactividad en:',
          warningSeconds,
          { hideClose: false },
        )
        .then((result: boolean | undefined) => {
          // true = usuario cerró con X; undefined = cerrado por actividad; false = cuenta regresiva llegó a 0
          if (result !== false) return;
          this.onInactivityExpired();
        });
    }, warningDelayMs);
  }

  private scheduleSessionTimer(): void {
    if (!this.hasSession() || this.isPermanentSession()) return;

    const sessionMinutes = Number(this.systemService.user?.userType?.sessionTime ?? 0);
    if (!sessionMinutes || sessionMinutes <= 0) return;

    const sessionMs = sessionMinutes * 60 * 1000;
    const startMs = this.getOrCreateSessionStartUtcMs();
    const endMs = startMs + sessionMs;
    // Mostrar aviso como máximo 1 minuto antes del fin; si queda menos (p. ej. tras refresh), usar el tiempo restante real.
    const maxWarningSeconds = 60;
    const warningStartMs = Math.max(startMs, endMs - maxWarningSeconds * 1000);
    const warningDelayMs = Math.max(0, warningStartMs - Date.now());

    this.sessionTimerId = setTimeout(() => {
      if (
        !this.hasSession() ||
        this.isPermanentSession() ||
        this.isExpiryWarningArmed
      )
        return;

      // Tiempo restante real hasta el fin de sesión (tras refresh puede ser < 60 s).
      const remainingMs = endMs - Date.now();
      if (remainingMs <= 0) {
        this.onSessionExpired();
        return;
      }
      const actualWarningSeconds = Math.max(1, Math.min(maxWarningSeconds, Math.floor(remainingMs / 1000)));

      this.isExpiryWarningArmed = true;
      this.activeTimerReason = 'session';

      void this.messageService
        .timerAlert(
          AlertEnum.Warning,
          'La sesión finalizará en:',
          actualWarningSeconds,
          {
            actionLabel: 'Continuar sesión',
            hideClose: true,
          },
        )
        .then((result: boolean | undefined) => {
          // true = usuario hizo clic en "Continuar sesión" → único momento en que se llama resume-session
          // false = cuenta regresiva llegó a 0 → cerrar sesión
          // undefined = aviso cerrado por actividad (volver a la ventana) → no hacer nada
          if (result === true) {
            void this.tryRenewSession();
            return;
          }
          if (result === false) {
            this.onSessionExpired();
          }
        });
    }, warningDelayMs);
  }

  private onInactivityExpired(): void {
    this.messageService.alert(
      AlertEnum.Warning,
      'La sesión se cerró por inactividad.',
      false,
    );
    this.userSessionService.logOutByExpiration();
  }

  private onSessionExpired(): void {
    this.messageService.alert(
      AlertEnum.Warning,
      'La sesión ha finalizado.',
      false,
    );
    this.userSessionService.logOutByExpiration();
  }

  private async tryRenewSession(): Promise<void> {
    if (!this.hasSession()) return;
    try {
      await this.userApiService.resumeSession();
      localStorage.setItem(this.sessionStartKey, String(Date.now()));
      this.isExpiryWarningArmed = false;
      this.activeTimerReason = null;
      this.refreshSessionTimers();
    } catch {
      this.userSessionService.logOutByExpiration();
    }
  }

  private clearSessionTimers(): void {
    if (this.inactivityTimerId) {
      clearTimeout(this.inactivityTimerId);
      this.inactivityTimerId = null;
    }
    if (this.sessionTimerId) {
      clearTimeout(this.sessionTimerId);
      this.sessionTimerId = null;
    }
  }

  private hasSession(): boolean {
    return !!localStorage.getItem(this.systemService.localStorageKeys.sessionToken);
  }

  private isPermanentSession(): boolean {
    return !!this.systemService.user?.userType?.permanentSession;
  }

  private getOrCreateSessionStartUtcMs(): number {
    const existing = Number(localStorage.getItem(this.sessionStartKey) ?? 0);
    if (existing && existing > 0) return existing;
    const now = Date.now();
    localStorage.setItem(this.sessionStartKey, String(now));
    return now;
  }

  private applyUserLanguage(user: User): void {
    if (!this.hasSession()) return;
    const userLanguage = user?.language?.keyCode;
    if (!userLanguage) return;
    const stored = localStorage.getItem(this.systemService.localStorageKeys.language);
    if (stored === userLanguage) return;
    this.systemService.changeLanguage(userLanguage);
  }
}
